import React, { useState, useEffect } from 'react';
import IASideBar from './IASideBar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import PolicyIcon from '@mui/icons-material/Policy';
import SyncIcon from '@mui/icons-material/Sync';
import SecurityIcon from '@mui/icons-material/Security';
import DescriptionIcon from '@mui/icons-material/Description';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';
import axiosInstance from '../../../services/AxiosService';
import companyService from '../../../services/CompanyService';
import policyService from '../../../services/PolicyService';

const PolicyLibrary = () => {
  // State for policies and related functionality
  const [policies, setPolicies] = useState([]);
  const [companies, setCompanies] = useState(['All Companies']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Snackbar notification state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const policyTypes = ['All Types', 'Security', 'Finance', 'Operations', 'HR', 'IT', 'Legal', 'Access Control', 'Password Policy', 'Information Security'];
  const policyStatuses = ['All Statuses', 'Active', 'Draft', 'Under Review', 'Archived'];

  // Filters
  const [selectedCompany, setSelectedCompany] = useState('All Companies');
  const [selectedType, setSelectedType] = useState('All Types');
  const [selectedStatus, setSelectedStatus] = useState('All Statuses');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyToEdit, setPolicyToEdit] = useState(null);

  // New state for password policy dialog
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordPolicyError, setPasswordPolicyError] = useState('');
  
  // Form states for adding/editing policies
  const [policyForm, setPolicyForm] = useState({
    name: '',
    type: '',
    company: [],
    description: '',
    content: '',
    status: 'Active',
    effective_date: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [addPolicyError, setAddPolicyError] = useState('');
  const [editPolicyError, setEditPolicyError] = useState('');
  
  // New form state for password policy settings
  const [passwordForm, setPasswordForm] = useState({
    length: 8,
    upper: true,
    lower: true,
    number: true,
    special_char: true,
    age: 90,
    history: 5,
    lockout_attempt: 3,
    lockout_duration: '30 minutes',
    mfa_enabled: false
  });
  
  // Flag to track if policy requires password settings
  const [requiresPasswordPolicy, setRequiresPasswordPolicy] = useState(false);
  const [tempPolicyData, setTempPolicyData] = useState(null);

  // New state for tracking existing password policies
  const [existingPasswordPolicies, setExistingPasswordPolicies] = useState([]);
  const [isUpdatingPolicy, setIsUpdatingPolicy] = useState(false);

  // Fetch companies and policies data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch companies using CompanyService
        const companiesData = await companyService.fetchCompanies();
        if (Array.isArray(companiesData)) {
          const companyNames = companiesData.map(company => company.COMPANY_NAME);
          setCompanies(['All Companies', ...companyNames]);
        } else {
          console.error("Unexpected format for companies data:", companiesData);
          setCompanies(['All Companies']);
        }
        
        // Fetch policies using PolicyService
        const policiesData = await policyService.fetchPolicies();
        
        // Transform policies data (ensure response structure is handled if needed)
        // Assuming fetchPolicies returns the array directly
        const transformedPolicies = policiesData.map(policy => ({
          id: policy.id,
          name: policy.POLICY_NAME,
          type: policy.POLICY_TYPE,
          company: policy.COMPANIES?.map(c => c.COMPANY_NAME).join(', ') || 'N/A',
          companiesArray: policy.COMPANIES || [],
          description: policy.DESCRIPTION,
          content: policy.CONTENT,
          lastUpdated: new Date(policy.LAST_MODIFIED || policy.CREATED_ON).toISOString().split('T')[0],
          status: policy.STATUS,
          effective_date: policy.EFFECTIVE_DATE,
          expiration_date: policy.EXPIRATION_DATE,
          documentUrl: policy.DOCUMENT_URL
        }));
        
        setPolicies(transformedPolicies);
      } catch (err) {
        // Error handling might need adjustment based on how PolicyService throws errors
        setError("Failed to load data. Please try again later."); 
        console.error("Error fetching initial data:", err); // Log the actual error from service
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
  };

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleOpenAddDialog = () => {
    setPolicyForm({
      name: '',
      type: '',
      company: [],
      description: '',
      content: '',
      status: 'Active',
      effective_date: new Date().toISOString().split('T')[0]
    });
    setSelectedFile(null);
    setAddPolicyError('');
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedFile(null);
    setAddPolicyError('');
    setPolicyForm({ 
      name: '', type: '', company: [], description: '', 
      content: '', status: 'Active', effective_date: new Date().toISOString().split('T')[0]
    });
  };

  const handleViewPolicy = (policy) => {
    setSelectedPolicy(policy);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
  };

  const handleOpenDeleteDialog = (policy) => {
    setSelectedPolicy(policy);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeletePolicy = async () => {
    try {
      await policyService.deletePolicy(selectedPolicy.id);
      setPolicies(policies.filter(policy => policy.id !== selectedPolicy.id));
      setOpenDeleteDialog(false);
      setSnackbarSeverity('success');
      setSnackbarMessage(`Policy "${selectedPolicy.name}" and all associated files deleted successfully.`);
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error deleting policy:", err);
      setSnackbarSeverity('error');
      setSnackbarMessage(`Failed to delete policy: ${err.message || 'Unknown error'}`);
      setSnackbarOpen(true);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setPolicyForm({
      ...policyForm,
      [name]: value
    });
    
    // Check if the policy type requires password settings
    if (name === 'type') {
      const policyType = value.toLowerCase();
      const needsPasswordSettings = policyType.includes('password') || 
                                  policyType.includes('access') || 
                                  policyType.includes('information security');
      console.log('Policy type:', value);
      console.log('Needs password settings:', needsPasswordSettings);
      setRequiresPasswordPolicy(needsPasswordSettings);
    }
  };

  const handleCompanySelection = (event) => {
    const { value } = event.target;
    setPolicyForm({
      ...policyForm,
      company: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0] || null);
  };

  // New handler for password policy form changes
  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    // Handle different types of fields
    if (name === 'upper' || name === 'lower' || name === 'number' || 
        name === 'special_char' || name === 'mfa_enabled') {
      setPasswordForm({
        ...passwordForm,
        [name]: e.target.checked
      });
    } else {
      setPasswordForm({
        ...passwordForm,
        [name]: value
      });
    }
  };

  // Close password policy dialog
  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setPasswordPolicyError('');
    setIsUpdatingPolicy(false);
    setExistingPasswordPolicies([]);
  };

  // Helper function to safely extract company info from different sources
  const extractCompanyInfo = (company, isFromTempData) => {
    if (!company) return { id: null, name: "Unknown Company" };
    
    if (isFromTempData) {
      return { 
        id: company.id, 
        name: company.name || "Unknown Company"
      };
    } else {
      return {
        id: company.id,
        name: company.COMPANY_NAME || "Unknown Company"
      };
    }
  };

  // Open password policy dialog and initialize form
  const handleOpenPasswordDialog = async () => {
    setOpenPasswordDialog(true);
    setPasswordPolicyError('');
    
    console.log('Opening password policy dialog for companies:', policyForm.company);
    
    // Check if we're coming from a previous update through tempPolicyData
    const companiesForPolicy = tempPolicyData?.company || policyForm.company;
    
    // Check if any of the selected companies already have password policies
    if (companiesForPolicy && companiesForPolicy.length > 0) {
      try {
        const existingPolicies = [];
        const companies = await companyService.fetchCompanies();
        console.log('Fetched companies:', companies);
        
        for (const companyName of companiesForPolicy) {
          console.log('Checking for password policy for company:', companyName);
          const company = companies.find(c => c.COMPANY_NAME === companyName);
          if (company) {
            console.log('Found company in database:', company);
            try {
              const policy = await policyService.getPasswordPolicyByCompany(company.id);
              console.log('Retrieved policy for company:', policy);
              if (policy) {
                existingPolicies.push({
                  companyName: companyName,
                  policy: policy
                });
              }
            } catch (err) {
              console.log(`No policy found for ${companyName}:`, err);
            }
          }
        }
        
        setExistingPasswordPolicies(existingPolicies);
        console.log('Existing password policies:', existingPolicies);
        
        if (existingPolicies.length > 0) {
          // Use the first existing policy to populate the form
          const firstPolicy = existingPolicies[0].policy;
          console.log('Using first existing policy to populate form:', firstPolicy);
          setPasswordForm({
            length: firstPolicy.LENGTH || 8,
            upper: firstPolicy.UPPER || true,
            lower: firstPolicy.LOWER || true,
            number: firstPolicy.NUMBER || true,
            special_char: firstPolicy.SPECIAL_CHAR || true,
            age: firstPolicy.AGE || 90,
            history: firstPolicy.HISTORY || 5,
            lockout_attempt: firstPolicy.LOCKOUT_ATTEMPT || 3,
            lockout_duration: firstPolicy.LOCKOUT_DURATION || '30 minutes',
            mfa_enabled: firstPolicy.MFA_ENABLED || false
          });
          setIsUpdatingPolicy(true);
        } else {
          console.log('No existing policies found, creating new ones');
          setIsUpdatingPolicy(false);
        }
      } catch (err) {
        console.error("Error checking for existing password policies:", err);
      }
    }
  };

  // Save password policy settings
  const handleSavePasswordPolicy = async () => {
    try {
      setPasswordPolicyError('');
      
      // Check if we have companies from either source
      const companiesForPolicy = tempPolicyData?.company || policyForm.company;
      
      if (!companiesForPolicy || companiesForPolicy.length === 0) {
        setPasswordPolicyError('Please select at least one company before saving password policy.');
        return;
      }

      // Now create and save the policy with the password settings
      await saveMainPolicy();
      
    } catch (err) {
      console.error("Error saving password policy:", err);
      setPasswordPolicyError(`Failed to save password policy: ${err.message || 'Please try again'}`);
    }
  };

  const handleAddPolicy = async () => {
    setAddPolicyError('');
    try {
      // Check if this policy type requires password settings
      console.log('Policy form type:', policyForm.type);
      console.log('requiresPasswordPolicy flag:', requiresPasswordPolicy);
      
      // Force check again to ensure it's caught
      const policyType = policyForm.type.toLowerCase();
      const shouldRequirePassword = policyType.includes('password') || 
                                    policyType.includes('access') || 
                                    policyType.includes('information security');
      console.log('Should require password (double-check):', shouldRequirePassword);
      
      if (requiresPasswordPolicy || shouldRequirePassword) {
        console.log('Opening password dialog...');
        // Save policy data temporarily and open password dialog
        setTempPolicyData({
          name: policyForm.name,
          type: policyForm.type,
          company: policyForm.company,
          description: policyForm.description,
          content: policyForm.content,
          status: policyForm.status,
          effective_date: policyForm.effective_date,
          file: selectedFile
        });
        handleOpenPasswordDialog();
        return;
      }
      
      // Regular policy save without password settings
      await saveMainPolicy();
    } catch (err) {
      console.error("Error adding policy:", err);
      // Extract error details if possible from the caught error
      const message = err.response?.data?.detail || 
                      (typeof err.response?.data === 'object' ? JSON.stringify(err.response.data) : null) ||
                      err.message || 
                      'Please check your input and try again.';
      setAddPolicyError(`Failed to add policy: ${message}`);
    }
  };

  // Save the main policy and handle password policy if needed
  const saveMainPolicy = async () => {
    const formData = new FormData();
    const policyData = tempPolicyData || policyForm;
    const isEditOperation = policyData.isEdit || false;
    
    // Special case: If this is an edit operation where we've already updated the main policy
    // and now just need to handle the password settings
    if (isEditOperation && tempPolicyData && tempPolicyData.skipMainUpdate) {
      console.log("Password-only update for existing policy");
      console.log("Policy data:", policyData);
      
      try {
        // Get the latest policy data to ensure we have current companies
        const responseData = await policyService.fetchPolicyById(policyData.id);
        console.log("Retrieved latest policy data:", responseData);
        
        // Check if we have companies from tempPolicyData - this helps avoid the problem with missing company IDs
        const useCompaniesFromTemp = tempPolicyData.companyIds && tempPolicyData.companyIds.length > 0;
        console.log("Using company IDs from tempPolicyData:", useCompaniesFromTemp);
        
        // Update password policies for all companies associated *now*
        if (requiresPasswordPolicy) {
          // Determine which companies to process:
          // 1. From responseData.COMPANIES (backend, most reliable but might be incomplete)
          // 2. From tempPolicyData.companyIds (stored during edit)
          const companiesToProcess = useCompaniesFromTemp ? 
                                    tempPolicyData.companyIds : 
                                    responseData.COMPANIES;
          
          if (!companiesToProcess || companiesToProcess.length === 0) {
            throw new Error("No companies available to associate with password policy");
          }

          for (const company of companiesToProcess) {
            // Extract company info safely with our helper function
            const { id: companyId, name: companyName } = extractCompanyInfo(company, useCompaniesFromTemp);
            
            // Check for company ID
            if (!companyId) {
              console.error(`Company object is missing ID for company ${companyName} during password update:`, company);
              // Skip this company as we cannot process without an ID
              continue; 
            }

            // Check if a password policy already exists for this company
            let existingPolicy = null;
            try {
              existingPolicy = await policyService.getPasswordPolicyByCompany(companyId);
            } catch (err) {
              console.log(`No existing password policy found for company ${companyName}, creating new one`);
            }

            const passwordPolicyData = {
              COMPANY_ID: companyId, // Use the verified company.id
              LENGTH: parseInt(passwordForm.length),
              UPPER: passwordForm.upper,
              LOWER: passwordForm.lower,
              NUMBER: passwordForm.number,
              SPECIAL_CHAR: passwordForm.special_char,
              AGE: parseInt(passwordForm.age),
              HISTORY: parseInt(passwordForm.history),
              LOCKOUT_ATTEMPT: parseInt(passwordForm.lockout_attempt),
              LOCKOUT_DURATION: passwordForm.lockout_duration,
              MFA_ENABLED: passwordForm.mfa_enabled
            };
            
            if (existingPolicy) {
              console.log(`Updating existing password policy for company ${companyName}`);
              await policyService.updatePasswordPolicy(existingPolicy.id, passwordPolicyData);
            } else {
              console.log(`Creating new password policy for company ${companyName}`);
              await policyService.createPasswordPolicy(passwordPolicyData);
            }
          }
        } else {
           console.log("Skipping password policy update step: requiresPasswordPolicy is false");
        }
        
        // Close dialogs and clean up *only on success*
        handleClosePasswordDialog();
        setTempPolicyData(null);
        // Display success message for the overall update
        setSnackbarSeverity('success');
        setSnackbarMessage(`Policy "${policyData.name}" updated successfully.`);
        setSnackbarOpen(true);
        return; // Exit after handling password-only update
      } catch (err) {
        console.error("Error in password-only update phase:", err);
        // *** Set error state for the password dialog and stop ***
        const message = err.response?.data?.detail || 
                        (typeof err.response?.data === 'object' ? JSON.stringify(err.response.data) : null) ||
                        err.message || 
                        'Failed to update password settings.';
        setPasswordPolicyError(`Error: ${message}`);
        // Do not close the password dialog here, let the user see the error and retry if possible
        return; 
      }
    }
    
    // Proceed with normal policy update/create flow
    console.log("Proceeding with normal policy create/update flow");
    formData.append('POLICY_NAME', policyData.name);
    formData.append('POLICY_TYPE', policyData.type);
    formData.append('DESCRIPTION', policyData.description);
    formData.append('CONTENT', policyData.content);
    formData.append('STATUS', policyData.status);
    formData.append('EFFECTIVE_DATE', policyData.effective_date);

    policyData.company.forEach(name => {
      formData.append('company_names', name);
    });

    if (tempPolicyData?.file || selectedFile) {
      formData.append('DOCUMENT_URL', tempPolicyData?.file || selectedFile);
    }

    let responseData;
    
    // Check if this is an update or new policy
    if (isEditOperation) {
      // Update existing policy
      responseData = await policyService.updatePolicy(policyData.id, formData);
    } else {
      // Create new policy
      responseData = await policyService.createPolicy(formData);
    }

    // Use the response data directly to create the new policy state object
    const updatedPolicy = {
      id: responseData.id,
      name: responseData.POLICY_NAME,
      type: responseData.POLICY_TYPE,
      company: responseData.COMPANIES?.map(c => c.COMPANY_NAME).join(', ') || 'N/A',
      companiesArray: responseData.COMPANIES || [],
      description: responseData.DESCRIPTION,
      content: responseData.CONTENT,
      lastUpdated: new Date(responseData.LAST_MODIFIED || responseData.CREATED_ON).toISOString().split('T')[0],
      status: responseData.STATUS,
      effective_date: responseData.EFFECTIVE_DATE,
      expiration_date: responseData.EXPIRATION_DATE,
      documentUrl: responseData.DOCUMENT_URL
    };

    // If password policy is required, check for existing policies and update or create as needed
    if (requiresPasswordPolicy && responseData.COMPANIES) {
      try {
        for (const company of responseData.COMPANIES) {
          // Check if a password policy already exists for this company
          let existingPolicy = null;
          try {
            existingPolicy = await policyService.getPasswordPolicyByCompany(company.id);
          } catch (err) {
            // If there's an error fetching the policy, we'll create a new one
            console.log(`No existing password policy found for company ${company.COMPANY_NAME}, creating new one`);
          }

          const passwordPolicyData = {
            COMPANY_ID: company.id,
            LENGTH: parseInt(passwordForm.length),
            UPPER: passwordForm.upper,
            LOWER: passwordForm.lower,
            NUMBER: passwordForm.number,
            SPECIAL_CHAR: passwordForm.special_char,
            AGE: parseInt(passwordForm.age),
            HISTORY: parseInt(passwordForm.history),
            LOCKOUT_ATTEMPT: parseInt(passwordForm.lockout_attempt),
            LOCKOUT_DURATION: passwordForm.lockout_duration,
            MFA_ENABLED: passwordForm.mfa_enabled
          };
          
          if (existingPolicy) {
            // Update existing policy
            console.log(`Updating existing password policy for company ${company.COMPANY_NAME}`);
            await policyService.updatePasswordPolicy(existingPolicy.id, passwordPolicyData);
          } else {
            // Create new policy
            console.log(`Creating new password policy for company ${company.COMPANY_NAME}`);
            await policyService.createPasswordPolicy(passwordPolicyData);
          }
        }
      } catch (err) {
        console.error("Error handling password policy:", err);
        // Even if password policy creation fails, we still added the main policy
        // Could add some user notification about password policy creation failure
        setSnackbarSeverity('warning');
        setSnackbarMessage(`Policy "${updatedPolicy.name}" saved, but encountered an issue saving password settings: ${err.message}`);
        setSnackbarOpen(true);
      }
    }

    // Update the policies state based on whether this is an edit or a new entry
    if (isEditOperation) {
      setPolicies(policies.map(policy => policy.id === updatedPolicy.id ? updatedPolicy : policy));
      // Don't show success message here if password update failed earlier
      if (!passwordPolicyError) { 
        setSnackbarSeverity('success');
        setSnackbarMessage(`Policy "${updatedPolicy.name}" updated successfully.`);
        setSnackbarOpen(true);
      }
    } else {
      setPolicies([...policies, updatedPolicy]);
       // Don't show success message here if password update failed earlier
      if (!passwordPolicyError) {
        setSnackbarSeverity('success');
        setSnackbarMessage(`Policy "${updatedPolicy.name}" added successfully.`);
        setSnackbarOpen(true);
      }
    }
    
    // Close all dialogs *unless* there was a password error during edit
    if (!isEditOperation || !passwordPolicyError) {
        handleCloseAddDialog();
        handleCloseEditDialog();
        handleClosePasswordDialog();
        setTempPolicyData(null);
    } else {
        // If it was an edit and there was a password error, keep password dialog open
        handleCloseAddDialog(); // Should be closed anyway
        handleCloseEditDialog(); // Should be closed anyway
        // Keep setTempPolicyData - might be needed if user retries from password dialog? Or clear it? Let's clear it.
        // setTempPolicyData(null); // Let's clear temp data to avoid stale state if they cancel password dialog later
    }
  };

  // --- Edit Dialog Handlers ---
  const handleOpenEditDialog = (policy) => {
    setPolicyToEdit(policy);
    setPolicyForm({
      name: policy.name || '',
      type: policy.type || '',
      company: policy.companiesArray?.map(c => c.COMPANY_NAME) || (typeof policy.company === 'string' ? policy.company.split(', ') : []), 
      description: policy.description || '',
      content: policy.content || '',
      status: policy.status || 'Active',
      effective_date: policy.effective_date || new Date().toISOString().split('T')[0]
    });
    setSelectedFile(null);
    setEditPolicyError('');
    handleCloseViewDialog();
    
    // Check if this policy type requires password settings
    const policyType = policy.type.toLowerCase();
    const needsPasswordSettings = policyType.includes('password') || 
                                 policyType.includes('access') || 
                                 policyType.includes('information security');
    console.log('Edit - Policy type:', policy.type);
    console.log('Edit - Needs password settings:', needsPasswordSettings);
    
    // Set the flag but don't open password dialog yet
    setRequiresPasswordPolicy(needsPasswordSettings);
    
    // Always open the regular edit dialog first
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setPolicyToEdit(null);
    setSelectedFile(null);
    setEditPolicyError('');
    setPolicyForm({ 
      name: '', type: '', company: [], description: '', 
      content: '', status: 'Active', effective_date: new Date().toISOString().split('T')[0]
    });
  };

  const handleUpdatePolicy = async () => {
    setEditPolicyError('');
    try {
      // Always update the regular policy fields first
      const formData = new FormData();
      formData.append('POLICY_NAME', policyForm.name);
      formData.append('POLICY_TYPE', policyForm.type);
      formData.append('DESCRIPTION', policyForm.description);
      formData.append('CONTENT', policyForm.content);
      formData.append('STATUS', policyForm.status);
      formData.append('EFFECTIVE_DATE', policyForm.effective_date);

      policyForm.company.forEach(name => {
        formData.append('company_names', name);
      });

      if (selectedFile) {
        formData.append('DOCUMENT_URL', selectedFile);
      }

      // Use PolicyService to update the policy
      const responseData = await policyService.updatePolicy(policyToEdit.id, formData);

      // Use the response data directly to update the policy state object
      const updatedPolicy = {
        id: responseData.id,
        name: responseData.POLICY_NAME,
        type: responseData.POLICY_TYPE,
        company: responseData.COMPANIES?.map(c => c.COMPANY_NAME).join(', ') || 'N/A',
        companiesArray: responseData.COMPANIES || [],
        description: responseData.DESCRIPTION,
        content: responseData.CONTENT,
        lastUpdated: new Date(responseData.LAST_MODIFIED).toISOString().split('T')[0],
        status: responseData.STATUS,
        effective_date: responseData.EFFECTIVE_DATE,
        expiration_date: responseData.EXPIRATION_DATE,
        documentUrl: responseData.DOCUMENT_URL
      };

      // Update the policies state
      setPolicies(policies.map(policy => policy.id === updatedPolicy.id ? updatedPolicy : policy));
      
      // Close the edit dialog
      handleCloseEditDialog();
      
      // After updating general details, check if we need to show password dialog
      console.log('After update - Policy type:', policyForm.type);
      console.log('After update - Requires password settings:', requiresPasswordPolicy);
      
      // Double check if the policy type requires password settings
      const policyType = policyForm.type.toLowerCase();
      const shouldRequirePassword = policyType.includes('password') || 
                                  policyType.includes('access') || 
                                  policyType.includes('information security');
                                  
      if (requiresPasswordPolicy || shouldRequirePassword) {
        console.log('Opening password dialog after general update...');
        console.log('Response data COMPANIES:', responseData.COMPANIES);
        
        // Ensure we have company data from the response
        const companyNames = responseData.COMPANIES?.map(c => c.COMPANY_NAME) || [];
        const companyIds = responseData.COMPANIES?.map(c => ({
          name: c.COMPANY_NAME,
          id: c.id
        })) || [];
        
        if (companyNames.length === 0) {
          console.warn('No companies found in the response data, using form data instead');
        }
        
        // Save policy data temporarily and open password dialog
        setTempPolicyData({
          id: responseData.id, // Use the response ID to ensure it's the latest
          name: responseData.POLICY_NAME,
          type: responseData.POLICY_TYPE,
          company: companyNames.length > 0 ? companyNames : policyForm.company,
          companyIds: companyIds, // Store the company IDs for direct access
          description: responseData.DESCRIPTION,
          content: responseData.CONTENT,
          status: responseData.STATUS,
          effective_date: responseData.EFFECTIVE_DATE,
          isEdit: true, // Flag to indicate this is an edit operation
          skipMainUpdate: true // Flag to indicate we've already updated the main policy
        });
        setRequiresPasswordPolicy(true);
        handleOpenPasswordDialog();
      } else {
        // Show success message for non-password policies
        setSnackbarSeverity('success');
        setSnackbarMessage(`Policy "${updatedPolicy.name}" updated successfully.`);
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Error updating policy:", err);
      // Extract error details if possible from the caught error
      const message = err.response?.data?.detail || 
                      (typeof err.response?.data === 'object' ? JSON.stringify(err.response.data) : null) ||
                      err.message || 
                      'Please check your input and try again.';
      setEditPolicyError(`Failed to update policy: ${message}`);
    }
  };
  // --- End Edit Dialog Handlers ---

  // Filter policies based on selected filters
  const filteredPolicies = policies.filter(policy => {
    const matchesCompany = selectedCompany === 'All Companies' || 
                           policy.company.includes(selectedCompany);
    const matchesType = selectedType === 'All Types' || policy.type === selectedType;
    const matchesStatus = selectedStatus === 'All Statuses' || policy.status === selectedStatus;
    const matchesSearch = policy.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          policy.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          policy.type.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCompany && matchesType && matchesStatus && matchesSearch;
  });

  // Create a theme consistent with your application
  const theme = createTheme({
    palette: {
      primary: {
        main: '#0288d1',
      },
      secondary: {
        main: '#4caf50',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
        },
      },
    },
  });

  // Main content of the page
  const customMainContent = (
    <div>
      <ResponsiveContainer>
        <mui.Box sx={{ padding: '24px' }}>
          {/* Breadcrumbs */}
          <mui.Breadcrumbs 
                        aria-label="breadcrumb"
                        sx={{
                            '& .MuiLink-root': {
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.secondary',
                                fontSize: '0.875rem',
                                transition: 'color 0.2s ease',
                                '&:hover': {
                                    color: 'primary.main'
                                }
                            }
                        }}
                    >
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Typography color="text.primary">Policy Library</mui.Typography>
                    </mui.Breadcrumbs>

          {/* Header */}
          <mui.Box sx={{ 
            mt: 3, 
            mb: 4, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PolicyIcon 
                sx={{ 
                  fontSize: 32, 
                  color: 'primary.main',
                  mr: 1,
                }} 
              />
              <mui.Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                Policy Library
              </mui.Typography>
            </mui.Box>
            <mui.Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.9rem',
              }}
            >
              Add New Policy
            </mui.Button>
          </mui.Box>

          {/* Search and filters */}
          <mui.Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              mb: 4, 
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <mui.Grid container spacing={2} alignItems="center">
              <mui.Grid item xs={12} md={4}>
                <mui.TextField
                  fullWidth
                  placeholder="Search policies..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <mui.InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </mui.InputAdornment>
                    ),
                    sx: {
                      borderRadius: 2,
                    }
                  }}
                />
              </mui.Grid>
              <mui.Grid item xs={12} md={8}>
                <mui.Box sx={{ display: 'flex', gap: 2 }}>
                  <mui.FormControl fullWidth size="small">
                    <mui.InputLabel id="company-filter-label">Company</mui.InputLabel>
                    <mui.Select
                      labelId="company-filter-label"
                      value={selectedCompany}
                      label="Company"
                      onChange={handleCompanyChange}
                      sx={{ borderRadius: 2 }}
                    >
                      {companies.map((company) => (
                        <mui.MenuItem key={company} value={company}>
                          {company}
                        </mui.MenuItem>
                      ))}
                    </mui.Select>
                  </mui.FormControl>
                  
                  <mui.FormControl fullWidth size="small">
                    <mui.InputLabel id="type-filter-label">Policy Type</mui.InputLabel>
                    <mui.Select
                      labelId="type-filter-label"
                      value={selectedType}
                      label="Policy Type"
                      onChange={handleTypeChange}
                      sx={{ borderRadius: 2 }}
                    >
                      {policyTypes.map((type) => (
                        <mui.MenuItem key={type} value={type}>
                          {type}
                        </mui.MenuItem>
                      ))}
                    </mui.Select>
                  </mui.FormControl>
                  
                  <mui.FormControl fullWidth size="small">
                    <mui.InputLabel id="status-filter-label">Status</mui.InputLabel>
                    <mui.Select
                      labelId="status-filter-label"
                      value={selectedStatus}
                      label="Status"
                      onChange={handleStatusChange}
                      sx={{ borderRadius: 2 }}
                    >
                      {policyStatuses.map((status) => (
                        <mui.MenuItem key={status} value={status}>
                          {status}
                        </mui.MenuItem>
                      ))}
                    </mui.Select>
                  </mui.FormControl>
                </mui.Box>
              </mui.Grid>
            </mui.Grid>
          </mui.Paper>

          {/* Policies Table */}
          <mui.Paper
            elevation={0}
            sx={{ 
              mb: 4, 
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            {loading ? (
              <mui.Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <mui.CircularProgress />
              </mui.Box>
            ) : error ? (
              <mui.Box sx={{ p: 3, textAlign: 'center' }}>
                <mui.Typography color="error">{error}</mui.Typography>
              </mui.Box>
            ) : (
              <mui.TableContainer>
                <mui.Table>
                  <mui.TableHead sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                    <mui.TableRow>
                      <mui.TableCell sx={{ fontWeight: 600 }}>Policy Name</mui.TableCell>
                      <mui.TableCell sx={{ fontWeight: 600 }}>Type</mui.TableCell>
                      <mui.TableCell sx={{ fontWeight: 600 }}>Company</mui.TableCell>
                      <mui.TableCell sx={{ fontWeight: 600 }}>Last Updated</mui.TableCell>
                      <mui.TableCell sx={{ fontWeight: 600 }}>Status</mui.TableCell>
                      <mui.TableCell sx={{ fontWeight: 600 }}>Document</mui.TableCell>
                      <mui.TableCell sx={{ fontWeight: 600 }}>Actions</mui.TableCell>
                    </mui.TableRow>
                  </mui.TableHead>
                  <mui.TableBody>
                    {filteredPolicies.length > 0 ? (
                      filteredPolicies.map((policy) => {
                        // Helper function to extract filename
                        const getFileName = (url) => {
                          if (!url) return null;
                          try {
                            const urlParts = url.split('/');
                            const encodedFilename = urlParts[urlParts.length - 1];
                            return decodeURIComponent(encodedFilename); // Decode URI component
                          } catch (e) {
                            console.error("Error decoding filename:", e);
                            return "Document"; // Fallback name
                          }
                        };
                        const fileName = getFileName(policy.documentUrl);

                        return (
                          <mui.TableRow key={policy.id} hover>
                            <mui.TableCell 
                              sx={{ 
                                cursor: 'pointer',
                                color: 'primary.main',
                                fontWeight: 500,
                              }}
                              onClick={() => handleViewPolicy(policy)}
                            >
                              {policy.name}
                            </mui.TableCell>
                            <mui.TableCell>{policy.type}</mui.TableCell>
                            <mui.TableCell>{policy.company}</mui.TableCell>
                            <mui.TableCell>{policy.lastUpdated}</mui.TableCell>
                            <mui.TableCell>
                              <mui.Chip 
                                label={policy.status} 
                                size="small"
                                color={
                                  policy.status === 'Active' ? 'success' :
                                  policy.status === 'Draft' ? 'default' :
                                  policy.status === 'Under Review' ? 'warning' :
                                  'error'
                                }
                                sx={{ 
                                  borderRadius: '4px',
                                  fontWeight: 500,
                                }}
                              />
                            </mui.TableCell>
                            <mui.TableCell>
                              {policy.documentUrl && fileName ? (
                                <mui.Link 
                                  href={policy.documentUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    textDecoration: 'none',
                                    color: 'primary.main',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                    } 
                                  }}
                                >
                                  <DescriptionIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                  <mui.Typography 
                                    variant="body2" 
                                    component="span"
                                    sx={{ 
                                      maxWidth: '150px', // Limit width to prevent long names breaking layout
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      verticalAlign: 'middle'
                                    }}
                                  >
                                    {fileName}
                                  </mui.Typography>
                                </mui.Link>
                              ) : (
                                <mui.Typography variant="body2" color="text.secondary">
                                  No Document
                                </mui.Typography>
                              )}
                            </mui.TableCell>
                            <mui.TableCell>
                              <mui.IconButton 
                                size="small" 
                                onClick={() => handleViewPolicy(policy)}
                                sx={{ color: 'primary.main' }}
                              >
                                <DescriptionIcon fontSize="small" />
                              </mui.IconButton>
                              <mui.IconButton 
                                size="small"
                                sx={{ color: 'primary.main' }}
                                onClick={() => handleOpenEditDialog(policy)}
                              >
                                <EditIcon fontSize="small" />
                              </mui.IconButton>
                              <mui.IconButton 
                                size="small"
                                onClick={() => handleOpenDeleteDialog(policy)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </mui.IconButton>
                            </mui.TableCell>
                          </mui.TableRow>
                        );
                      })
                    ) : (
                      <mui.TableRow>
                        <mui.TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <mui.Typography variant="body1" color="text.secondary">
                            No policies found matching your criteria.
                          </mui.Typography>
                        </mui.TableCell>
                      </mui.TableRow>
                    )}
                  </mui.TableBody>
                </mui.Table>
              </mui.TableContainer>
            )}
          </mui.Paper>
        </mui.Box>
      </ResponsiveContainer>
      
      {/* Add/Edit Policy Dialog */}
      <mui.Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <mui.DialogTitle
          sx={{
            py: 3,
            px: 3,
            fontWeight: 600,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          Add New Policy
        </mui.DialogTitle>
        <mui.DialogContent sx={{ p: 3, pt: 3 }}>
          {addPolicyError && (
            <mui.Alert severity="error" sx={{ mb: 2 }}>
              {addPolicyError}
            </mui.Alert>
          )}
          <mui.Grid container spacing={3}>
            <mui.Grid item xs={12} md={6}>
              <mui.TextField
                name="name"
                label="Policy Name"
                value={policyForm.name}
                onChange={handleFormChange}
                fullWidth
                required
                margin="dense"
                variant="outlined"
              />
            </mui.Grid>
            <mui.Grid item xs={12} md={6}>
              <mui.FormControl fullWidth margin="dense">
                <mui.InputLabel id="policy-company-label">Companies *</mui.InputLabel>
                <mui.Select
                  labelId="policy-company-label"
                  name="company"
                  multiple
                  value={policyForm.company}
                  label="Companies *"
                  onChange={handleCompanySelection}
                  renderValue={(selected) => selected.join(', ')}
                  required
                >
                  {companies.filter(c => c !== 'All Companies').map((company) => (
                    <mui.MenuItem key={company} value={company}>
                      <mui.Checkbox checked={policyForm.company.indexOf(company) > -1} />
                      <mui.ListItemText primary={company} />
                    </mui.MenuItem>
                  ))}
                </mui.Select>
              </mui.FormControl>
            </mui.Grid>
            <mui.Grid item xs={12} md={6}>
               <mui.FormControl fullWidth margin="dense" required>
                <mui.InputLabel id="policy-type-label">Policy Type</mui.InputLabel>
                <mui.Select
                  labelId="policy-type-label"
                  name="type"
                  value={policyForm.type}
                  label="Policy Type"
                  onChange={handleFormChange}
                  required
                >
                  {policyTypes.filter(type => type !== 'All Types').map((type) => (
                    <mui.MenuItem key={type} value={type}>
                      {type}
                    </mui.MenuItem>
                  ))}
                </mui.Select>
              </mui.FormControl>
            </mui.Grid>
            <mui.Grid item xs={12} md={6}>
              <mui.FormControl fullWidth margin="dense" required>
                <mui.InputLabel id="policy-status-label">Status</mui.InputLabel>
                <mui.Select
                  labelId="policy-status-label"
                  name="status"
                  value={policyForm.status}
                  label="Status"
                  onChange={handleFormChange}
                  required
                >
                  {policyStatuses.filter(status => status !== 'All Statuses').map((status) => (
                    <mui.MenuItem key={status} value={status}>
                      {status}
                    </mui.MenuItem>
                  ))}
                </mui.Select>
              </mui.FormControl>
            </mui.Grid>
            <mui.Grid item xs={12} md={6}>
              <mui.TextField
                name="effective_date"
                label="Effective Date"
                type="date"
                value={policyForm.effective_date}
                onChange={handleFormChange}
                fullWidth
                required
                margin="dense"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </mui.Grid>
            <mui.Grid item xs={12} md={6}>
               <mui.FormControl fullWidth margin="dense">
                 <mui.Typography variant="body2" color="textSecondary" gutterBottom>
                   Policy Document (Optional)
                 </mui.Typography>
                 <mui.Button
                    variant="outlined"
                    component="label"
                    startIcon={<FileUploadIcon />}
                    fullWidth
                    sx={{ textTransform: 'none', justifyContent: 'flex-start', color: 'text.secondary' }}
                 >
                    {selectedFile ? selectedFile.name : 'Upload Document'}
                    <input
                      type="file"
                      hidden
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.txt"
                    />
                 </mui.Button>
                 {selectedFile && (
                     <mui.Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                       Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </mui.Typography>
                 )}
               </mui.FormControl>
            </mui.Grid>
            <mui.Grid item xs={12}>
              <mui.TextField
                name="description"
                label="Description"
                value={policyForm.description}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={2}
                margin="dense"
                variant="outlined"
              />
            </mui.Grid>
            <mui.Grid item xs={12}>
              <mui.TextField
                name="content"
                label="Policy Content"
                value={policyForm.content}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={8}
                margin="dense"
                variant="outlined"
                placeholder="Enter the policy text here, or upload a document."
              />
            </mui.Grid>
          </mui.Grid>
        </mui.DialogContent>
        <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
          <mui.Button
            onClick={handleCloseAddDialog}
              sx={{ 
              color: 'text.secondary',
              textTransform: 'none',
            }}
          >
            Cancel
          </mui.Button>
            <mui.Button
            onClick={handleAddPolicy}
              variant="contained"
              disableElevation
            sx={{
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Save Policy
          </mui.Button>
        </mui.DialogActions>
      </mui.Dialog>
      
      {/* View Policy Dialog */}
      <mui.Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }
        }}
      >
        {selectedPolicy && (
          <>
            <mui.DialogTitle
              sx={{
                py: 2,
                px: 3,
                fontWeight: 600,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'grey.50',
              }}
            >
              <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PolicyIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                <mui.Typography variant="h6" component="span" fontWeight={600}>
                  {selectedPolicy.name}
                </mui.Typography>
              </mui.Box>
              {selectedPolicy.documentUrl ? (
                <mui.Button
                  startIcon={<DownloadIcon />}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    textTransform: 'none', 
                    borderRadius: 2,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      borderColor: 'primary.dark',
                    }
                  }}
                  href={selectedPolicy.documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Document
                </mui.Button>
              ) : (
                 <mui.Button
                     startIcon={<DownloadIcon />}
                     variant="outlined"
                     size="small"
                     sx={{ textTransform: 'none', borderRadius: 2 }}
                     disabled
                 >
                     No Document
                 </mui.Button>
              )}
            </mui.DialogTitle>
            <mui.DialogContent sx={{ p: 3 }}>
              <mui.Box sx={{ mb: 3 }}>
                <mui.Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Policy Overview
                </mui.Typography>
                <mui.Grid container spacing={3}>
                  <mui.Grid item xs={12} sm={6} md={4}>
                    <mui.Typography variant="caption" color="text.secondary" display="block">
                      Company
                    </mui.Typography>
                    <mui.Typography variant="body1" fontWeight={500}>
                      {selectedPolicy.company || 'N/A'}
                    </mui.Typography>
                  </mui.Grid>
                  <mui.Grid item xs={12} sm={6} md={4}>
                    <mui.Typography variant="caption" color="text.secondary" display="block">
                      Policy Type
                    </mui.Typography>
                    <mui.Typography variant="body1" fontWeight={500}>
                      {selectedPolicy.type || 'N/A'}
                    </mui.Typography>
                  </mui.Grid>
                  <mui.Grid item xs={12} sm={6} md={4}>
                    <mui.Typography variant="caption" color="text.secondary" display="block">
                      Status
                    </mui.Typography>
                    <mui.Chip 
                      label={selectedPolicy.status || 'N/A'} 
                      size="small"
                      color={
                        selectedPolicy.status === 'Active' ? 'success' :
                        selectedPolicy.status === 'Draft' ? 'info' :
                        selectedPolicy.status === 'Under Review' ? 'warning' :
                        'default'
                      }
                      sx={{ 
                        borderRadius: '4px',
                        fontWeight: 500,
                        height: 'auto',
                        padding: '2px 8px'
                      }}
                    />
                  </mui.Grid>
                  <mui.Grid item xs={12} sm={6} md={4}>
                    <mui.Typography variant="caption" color="text.secondary" display="block">
                      Effective Date
                    </mui.Typography>
                    <mui.Typography variant="body1" fontWeight={500}>
                      {selectedPolicy.effective_date ? new Date(selectedPolicy.effective_date).toLocaleDateString() : 'N/A'}
                    </mui.Typography>
                  </mui.Grid>
                  <mui.Grid item xs={12} sm={6} md={4}>
                    <mui.Typography variant="caption" color="text.secondary" display="block">
                      Last Updated
                    </mui.Typography>
                    <mui.Typography variant="body1" fontWeight={500}>
                      {selectedPolicy.lastUpdated ? new Date(selectedPolicy.lastUpdated).toLocaleDateString() : 'N/A'}
                    </mui.Typography>
                  </mui.Grid>
                </mui.Grid>
              </mui.Box>

              {selectedPolicy.description && (
                <>
                  <mui.Divider sx={{ my: 3 }} />
                  <mui.Box sx={{ mb: 3 }}>
                    <mui.Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Description
                    </mui.Typography>
                    <mui.Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedPolicy.description}
                    </mui.Typography>
                  </mui.Box>
                </>
              )}
              
              <mui.Divider sx={{ my: 3 }} />
              
              <mui.Box>
                <mui.Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Policy Content
                </mui.Typography>

                <mui.Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundColor: 'grey.50',
                    borderRadius: 1.5,
                    maxHeight: '350px',
                    overflow: 'auto',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <mui.Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {selectedPolicy.content || 
                      (selectedPolicy.documentUrl ? 
                        <mui.Typography variant="body2" component="em" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                           Policy content is in the uploaded document. 
                         </mui.Typography>
                        : 
                        <mui.Typography variant="body2" component="em" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                           No content provided. 
                         </mui.Typography>
                      )
                    }
                  </mui.Typography>
                </mui.Paper>
              </mui.Box>
            </mui.DialogContent>
            <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)', backgroundColor: 'grey.50' }}>
              <mui.Button
                onClick={handleCloseViewDialog}
                sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  borderRadius: 2,
                }}
              >
                Close
              </mui.Button>
              <mui.Button
                variant="contained"
                disableElevation
                startIcon={<EditIcon />}
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                }}
                onClick={() => handleOpenEditDialog(selectedPolicy)}
              >
                Edit Policy
              </mui.Button>
            </mui.DialogActions>
          </>
        )}
      </mui.Dialog>
      
      {/* Delete Confirmation Dialog */}
      <mui.Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: '450px', // Slightly wider for better spacing
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)', // Enhanced shadow for premium look
          }
        }}
      >
        {selectedPolicy && (
          <>
            <mui.DialogTitle
              sx={{
                py: 2.5,
                px: 3,
                fontWeight: 600,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                backgroundColor: 'grey.50', // Consistent with other dialogs
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <DeleteIcon color="error" fontSize="small" />
              Confirm Deletion
            </mui.DialogTitle>
            <mui.DialogContent sx={{ p: 3, pt: 3 }}>
              <mui.Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                mb: 2, 
                pt: 1 
              }}>
                <mui.Avatar 
                  sx={{ 
                    bgcolor: 'error.light', 
                    width: 56, 
                    height: 56, 
                    mb: 2 
                  }}
                >
                  <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
                </mui.Avatar>
                <mui.Typography variant="h6" align="center" gutterBottom>
                  Delete "{selectedPolicy.name}"?
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary" align="center">
                  This action cannot be undone. This policy will be permanently removed from the system
                  {selectedPolicy.documentUrl ? ", including any attached documents." : "."}
                </mui.Typography>
              </mui.Box>
            </mui.DialogContent>
            <mui.DialogActions sx={{ 
              px: 3, 
              py: 2.5, 
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              backgroundColor: 'grey.50', // Consistent with other dialogs
              justifyContent: 'center', // Center buttons for emphasis
              gap: 1 // Add space between buttons
            }}>
              <mui.Button
                onClick={handleCloseDeleteDialog}
                variant="outlined"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 3,
                  minWidth: '120px', // Fixed width for better alignment
                }}
              >
                Cancel
              </mui.Button>
              <mui.Button
                onClick={handleDeletePolicy}
                variant="contained"
                color="error"
                disableElevation
                startIcon={<DeleteIcon />}
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  minWidth: '120px', // Fixed width for better alignment
                  '&:hover': {
                    bgcolor: 'error.dark',
                  }
                }}
              >
                Delete
              </mui.Button>
            </mui.DialogActions>
          </>
        )}
      </mui.Dialog>

      {/* Edit Policy Dialog */}
      <mui.Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <mui.DialogTitle
          sx={{
            py: 3,
            px: 3,
            fontWeight: 600,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          Edit Policy
        </mui.DialogTitle>
        <mui.DialogContent sx={{ p: 3, pt: 3 }}>
          {editPolicyError && (
            <mui.Alert severity="error" sx={{ mb: 2 }}>
              {editPolicyError}
            </mui.Alert>
          )}
          <mui.Grid container spacing={3}>
            <mui.Grid item xs={12} md={6}>
              <mui.TextField
                name="name"
                label="Policy Name"
                value={policyForm.name}
                onChange={handleFormChange}
                fullWidth
                required
                margin="dense"
                variant="outlined"
              />
            </mui.Grid>
            <mui.Grid item xs={12} md={6}>
              <mui.FormControl fullWidth margin="dense">
                <mui.InputLabel id="edit-policy-company-label">Companies *</mui.InputLabel>
                <mui.Select
                  labelId="edit-policy-company-label"
                  name="company"
                  multiple
                  value={policyForm.company}
                  label="Companies *"
                  onChange={handleCompanySelection}
                  renderValue={(selected) => selected.join(', ')}
                  required
                >
                  {companies.filter(c => c !== 'All Companies').map((company) => (
                    <mui.MenuItem key={company} value={company}>
                      <mui.Checkbox checked={policyForm.company.indexOf(company) > -1} />
                      <mui.ListItemText primary={company} />
                    </mui.MenuItem>
                  ))}
                </mui.Select>
              </mui.FormControl>
            </mui.Grid>
            <mui.Grid item xs={12} md={6}>
               <mui.FormControl fullWidth margin="dense" required>
                <mui.InputLabel id="edit-policy-type-label">Policy Type</mui.InputLabel>
                <mui.Select
                  labelId="edit-policy-type-label"
                  name="type"
                  value={policyForm.type}
                  label="Policy Type"
                  onChange={handleFormChange}
                  required
                >
                  {policyTypes.filter(type => type !== 'All Types').map((type) => (
                    <mui.MenuItem key={type} value={type}>
                      {type}
                    </mui.MenuItem>
                  ))}
                </mui.Select>
              </mui.FormControl>
            </mui.Grid>
            <mui.Grid item xs={12} md={6}>
              <mui.FormControl fullWidth margin="dense" required>
                <mui.InputLabel id="edit-policy-status-label">Status</mui.InputLabel>
                <mui.Select
                  labelId="edit-policy-status-label"
                  name="status"
                  value={policyForm.status}
                  label="Status"
                  onChange={handleFormChange}
                  required
                >
                  {policyStatuses.filter(status => status !== 'All Statuses').map((status) => (
                    <mui.MenuItem key={status} value={status}>
                      {status}
                    </mui.MenuItem>
                  ))}
                </mui.Select>
              </mui.FormControl>
            </mui.Grid>
            <mui.Grid item xs={12} md={6}>
              <mui.TextField
                name="effective_date"
                label="Effective Date"
                type="date"
                value={policyForm.effective_date}
                onChange={handleFormChange}
                fullWidth
                required
                margin="dense"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </mui.Grid>
            <mui.Grid item xs={12} md={6}>
               <mui.FormControl fullWidth margin="dense">
                 <mui.Typography variant="body2" color="textSecondary" gutterBottom>
                   Policy Document (Optional)
                 </mui.Typography>
                 {/* Display current document name if one exists */}
                 {policyToEdit?.documentUrl && !selectedFile && (
                   <mui.Box sx={{ mb: 1 }}>
                     <mui.Typography variant="caption" color="primary" display="block">
                       Current document: {policyToEdit.documentUrl.split('/').pop()}
                     </mui.Typography>
                     <mui.Typography variant="caption" color="text.secondary" display="block">
                       Upload a new file to replace the current one.
                     </mui.Typography>
                   </mui.Box>
                 )}
                 <mui.Button
                    variant="outlined"
                    component="label"
                    startIcon={<FileUploadIcon />}
                    fullWidth
                    sx={{ textTransform: 'none', justifyContent: 'flex-start', color: 'text.secondary' }}
                 >
                    {selectedFile ? selectedFile.name : 'Upload New Document'}
                    <input
                      type="file"
                      hidden
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.txt"
                    />
                 </mui.Button>
                 {selectedFile && (
                     <mui.Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                       Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </mui.Typography>
                 )}
               </mui.FormControl>
            </mui.Grid>
            <mui.Grid item xs={12}>
              <mui.TextField
                name="description"
                label="Description"
                value={policyForm.description}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={2}
                margin="dense"
                variant="outlined"
              />
            </mui.Grid>
            <mui.Grid item xs={12}>
              <mui.TextField
                name="content"
                label="Policy Content"
                value={policyForm.content}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={8}
                margin="dense"
                variant="outlined"
                placeholder="Enter the policy text here, or upload a document."
              />
            </mui.Grid>
          </mui.Grid>
        </mui.DialogContent>
        <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
          <mui.Button
            onClick={handleCloseEditDialog}
              sx={{ 
              color: 'text.secondary',
              textTransform: 'none',
            }}
          >
            Cancel
          </mui.Button>
            <mui.Button
            onClick={handleUpdatePolicy}
              variant="contained"
              disableElevation
            sx={{
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Update Policy
          </mui.Button>
        </mui.DialogActions>
      </mui.Dialog>

      {/* Password Policy Dialog */}
      <mui.Dialog
        open={openPasswordDialog}
        onClose={handleClosePasswordDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <mui.DialogTitle
          sx={{
            py: 3,
            px: 3,
            fontWeight: 600,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <LockIcon color="primary" />
          {tempPolicyData?.isEdit 
            ? 'Edit Password Policy Settings'
            : (isUpdatingPolicy ? 'Update Password Policy Settings' : 'Create Password Policy Settings')}
        </mui.DialogTitle>
        <mui.DialogContent sx={{ p: 3, pt: 3 }}>
          {passwordPolicyError && (
            <mui.Alert severity="error" sx={{ mb: 2 }}>
              {passwordPolicyError}
            </mui.Alert>
          )}
          
          <mui.Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            {tempPolicyData?.isEdit
              ? (isUpdatingPolicy 
                  ? `You're editing a policy that requires password settings. We found existing password policies for ${existingPasswordPolicies.map(p => p.companyName).join(', ')}. You can update these settings below.`
                  : `You're editing a policy that requires password settings. Please configure the password policy for the selected companies.`)
              : (isUpdatingPolicy 
                  ? `This policy type requires password settings. We found existing password policies for ${existingPasswordPolicies.map(p => p.companyName).join(', ')}. You can update these settings below.`
                  : 'This policy type requires additional password settings. Please configure the password policy for the selected companies.')}
          </mui.Typography>
          
          {/* Associated Companies Section - Enhanced Styling */}
          <mui.Box sx={{ 
            mb: 3, // Increased bottom margin
            p: 2.5, // Increased padding
            backgroundColor: 'grey.100', // Softer background
            borderRadius: 2, // Match dialog border radius
            border: '1px solid',
            borderColor: 'grey.200' // Subtle border
          }}>
            <mui.Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
              <SecurityIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.8, color: 'primary.main' }}/>
              Associated Companies
            </mui.Typography>
            
            {/* Conditionally render Chips or a list */}
            {(tempPolicyData?.company || policyForm.company)?.length > 0 ? (
              (tempPolicyData?.company || policyForm.company).length <= 5 ? (
                // Use Chips for a small number of companies
                <mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(tempPolicyData?.company || policyForm.company).map((companyName) => (
                    <mui.Chip key={companyName} label={companyName} size="small" sx={{ borderRadius: '4px' }} />
                  ))}
                </mui.Box>
              ) : (
                // Use Typography list for many companies
                <mui.Typography variant="body2" color="text.secondary">
                  {(tempPolicyData?.company || policyForm.company).join(', ')}
                </mui.Typography>
              )
            ) : (
              <mui.Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No companies selected
              </mui.Typography>
            )}
          </mui.Box>
          
          {existingPasswordPolicies.length > 0 && (tempPolicyData?.company || policyForm.company).length > existingPasswordPolicies.length && (
            <mui.Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}> {/* Added border radius */}
              New password policies will be created for: {(tempPolicyData?.company || policyForm.company)
                .filter(c => !existingPasswordPolicies.some(p => p.companyName === c))
                .join(', ')}
            </mui.Alert>
          )}
          
          <mui.Grid container spacing={3}>
            {/* Password length */}
            <mui.Grid item xs={12} md={6}>
              <mui.TextField
                name="length"
                label="Minimum Password Length"
                type="number"
                value={passwordForm.length}
                onChange={handlePasswordFormChange}
                fullWidth
                required
                margin="dense"
                variant="outlined"
                InputProps={{ inputProps: { min: 6, max: 24 } }}
              />
            </mui.Grid>
            
            {/* Password age */}
            <mui.Grid item xs={12} md={6}>
              <mui.TextField
                name="age"
                label="Password Age (days)"
                type="number"
                value={passwordForm.age}
                onChange={handlePasswordFormChange}
                fullWidth
                required
                margin="dense"
                variant="outlined"
                helperText="Number of days before password expires"
                InputProps={{ inputProps: { min: 1, max: 365 } }}
              />
            </mui.Grid>
            
            {/* Password history */}
            <mui.Grid item xs={12} md={6}>
              <mui.TextField
                name="history"
                label="Password History"
                type="number"
                value={passwordForm.history}
                onChange={handlePasswordFormChange}
                fullWidth
                required
                margin="dense"
                variant="outlined"
                helperText="Number of previous passwords to remember"
                InputProps={{ inputProps: { min: 0, max: 24 } }}
              />
            </mui.Grid>
            
            {/* Lockout attempts */}
            <mui.Grid item xs={12} md={6}>
              <mui.TextField
                name="lockout_attempt"
                label="Lockout Attempts"
                type="number"
                value={passwordForm.lockout_attempt}
                onChange={handlePasswordFormChange}
                fullWidth
                required
                margin="dense"
                variant="outlined"
                helperText="Number of failed attempts before account lockout"
                InputProps={{ inputProps: { min: 1, max: 10 } }}
              />
            </mui.Grid>
            
            {/* Lockout duration */}
            <mui.Grid item xs={12} md={6}>
              <mui.FormControl fullWidth margin="dense">
                <mui.InputLabel id="lockout-duration-label">Lockout Duration</mui.InputLabel>
                <mui.Select
                  labelId="lockout-duration-label"
                  name="lockout_duration"
                  value={passwordForm.lockout_duration}
                  label="Lockout Duration"
                  onChange={handlePasswordFormChange}
                >
                  <mui.MenuItem value="15 minutes">15 minutes</mui.MenuItem>
                  <mui.MenuItem value="30 minutes">30 minutes</mui.MenuItem>
                  <mui.MenuItem value="1 hour">1 hour</mui.MenuItem>
                  <mui.MenuItem value="8 hours">8 hours</mui.MenuItem>
                  <mui.MenuItem value="24 hours">24 hours</mui.MenuItem>
                  <mui.MenuItem value="Until admin reset">Until admin reset</mui.MenuItem>
                </mui.Select>
              </mui.FormControl>
            </mui.Grid>
            
            {/* MFA */}
            <mui.Grid item xs={12} md={6}>
              <mui.FormControlLabel
                control={
                  <mui.Checkbox
                    checked={passwordForm.mfa_enabled}
                    onChange={handlePasswordFormChange}
                    name="mfa_enabled"
                    color="primary"
                  />
                }
                label="Enable Multi-Factor Authentication"
              />
            </mui.Grid>
            
            {/* Complexity group */}
            <mui.Grid item xs={12}>
              <mui.Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Password Complexity Requirements
              </mui.Typography>
              <mui.Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 2, 
                p: 2, 
                border: '1px solid rgba(0, 0, 0, 0.12)', 
                borderRadius: 1 
              }}>
                <mui.FormControlLabel
                  control={
                    <mui.Checkbox
                      checked={passwordForm.upper}
                      onChange={handlePasswordFormChange}
                      name="upper"
                      color="primary"
                    />
                  }
                  label="Uppercase Letters"
                />
                
                <mui.FormControlLabel
                  control={
                    <mui.Checkbox
                      checked={passwordForm.lower}
                      onChange={handlePasswordFormChange}
                      name="lower"
                      color="primary"
                    />
                  }
                  label="Lowercase Letters"
                />
                
                <mui.FormControlLabel
                  control={
                    <mui.Checkbox
                      checked={passwordForm.number}
                      onChange={handlePasswordFormChange}
                      name="number"
                      color="primary"
                    />
                  }
                  label="Numbers"
                />
                
                <mui.FormControlLabel
                  control={
                    <mui.Checkbox
                      checked={passwordForm.special_char}
                      onChange={handlePasswordFormChange}
                      name="special_char"
                      color="primary"
                    />
                  }
                  label="Special Characters"
                />
              </mui.Box>
            </mui.Grid>
          </mui.Grid>
        </mui.DialogContent>
        <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
          <mui.Button
            onClick={handleClosePasswordDialog}
            sx={{ 
              color: 'text.secondary',
              textTransform: 'none',
            }}
          >
            Back
          </mui.Button>
          <mui.Button
            onClick={handleSavePasswordPolicy}
            variant="contained"
            disableElevation
            sx={{
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            {isUpdatingPolicy ? 'Update and Continue' : 'Save and Continue'}
          </mui.Button>
        </mui.DialogActions>
      </mui.Dialog>
    </div>
  );

  // Helper function to handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <IASideBar mainContent={customMainContent} />
      <mui.Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <mui.Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbarMessage}
        </mui.Alert>
      </mui.Snackbar>
    </ThemeProvider>
  );
};

export default PolicyLibrary;
